import { styled } from "@mui/material";
import ComboBoxField from "controls/global/combo-box-field";
import { SelectFieldOption } from "controls/global/select-field/SelectInput";
import TextInputField from "controls/global/text-input-field";
import { buildTransCodeKey } from "pages/file/utils/helper";
import React, {
  useState,
} from "react";
import { useTransCodeContext } from "utils/context/CompanyTransCodeContext";
import useCollateralMortgageTransCode from "utils/custom-hooks/useCollateralMortgageTransCode";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { ProductType } from "utils/data/enum";

type Props = {
  schema: string;
  disabled: boolean;
  formType: string;
  isTransCodeRequired: boolean | undefined;
  // Collateral Mortgage is only offered on a Loan jacket once the file already
  // has an issued Loan jacket for it to attach to.
  allowCollateralMortgage?: boolean;
  // The jacket already carries the Collateral Mortgage code, so it must stay in the
  // list even when it can no longer be picked.
  isCollateralMortgage?: boolean;
};

const Container = styled("div")({
  flex: 1,
  maxWidth: "500px",
  minWidth: "120px",
  width: "unset",
});

const JacketTransCode = ({
  schema,
  disabled,
  formType,
  isTransCodeRequired,
  allowCollateralMortgage = false,
  isCollateralMortgage = false,
}: Props) => {
  const [transCodes, setTransCodes] = useState<SelectFieldOption[]>([]);
  const {
    getValues,
    setValue,
    trigger,
    formState: { errors },
    clearErrors,
  } = useFormWrapper();
  const [, { getTransCodes }] = useTransCodeContext();
  const collateralMortgageTransCode = useCollateralMortgageTransCode();

  const fieldName = `${schema}.pricingDetail.transCodeKey`;
  const transCodePropName = `${schema}.pricingDetail.transCode`;
  const transCodeDescPropName = `${schema}.pricingDetail.transCodeDescription`;
  const currentStateAbbr: string = getValues("properties.0.state.abbr");

  const handleOnChange = (
    _: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>,
    selectedValue: any
  ) => {    
    if (!selectedValue) return;    
    setValue(fieldName, selectedValue?.text);
    setValue(transCodePropName, selectedValue?.originalValue);
    setValue(transCodeDescPropName, selectedValue?.desc);
  };

  const handleOnBlur = async (e: any) => {
    const currentTransCode = getValues(transCodePropName);
    const currentTransCodeDesc = getValues(transCodeDescPropName);

    setValue(fieldName, buildTransCodeKey(currentTransCode, currentTransCodeDesc));
    setValue(transCodePropName, getValues(transCodePropName));
    setValue(transCodeDescPropName, getValues(transCodeDescPropName));
    const fieldErrors = await trigger(fieldName);

    if (currentTransCode && fieldErrors && errors[fieldName]) {
      clearErrors(fieldName);
    }
  };

  React.useEffect(() => {
    async function getTransCodesAsync() {
      const data = await getTransCodes(
        formType,
        currentStateAbbr,
        ProductType.Jacket
      );
      const allCodes: SelectFieldOption[] =
        data?.map((t) => {
          const key = buildTransCodeKey(t.transCode, t.transCodeDescription);
          return {
            text: key,
            value: key,
            desc: t.transCodeDescription,
            originalValue: t.transCode,
          };
        }) || [];

      // Nothing to hide when the state has no configured collateral trans code. When the
      // jacket already carries the code, keep it visible but non-selectable instead of
      // dropping the value out of the list (same idea as getProductLocations in
      // pages/file/utils/helper.ts).
      const codes =
        allowCollateralMortgage || !collateralMortgageTransCode
          ? allCodes
          : allCodes
            .filter(
              (c) =>
                c.originalValue !== collateralMortgageTransCode ||
                isCollateralMortgage
            )
            .map((c) =>
              c.originalValue === collateralMortgageTransCode
                ? { ...c, disabled: true }
                : c
            );

      if (codes.length === 0) {
        setTransCodes([]);
        return;
      }

      // Auto-select is a convenience for a jacket that has no trans code yet — it must
      // never overwrite one already on the jacket, and never land on a disabled code.
      const selectableCodes = codes.filter((c) => !c.disabled);

      if (selectableCodes.length === 1 && !getValues(transCodePropName)) {
        setValue(fieldName, selectableCodes[0].value);
        setValue(transCodePropName, selectableCodes[0].originalValue);
        setValue(transCodeDescPropName, selectableCodes[0].desc);
      }

      setTransCodes(codes);
    }

    if (!formType || disabled || !isTransCodeRequired) return;
    getTransCodesAsync();
  }, [
    allowCollateralMortgage,
    collateralMortgageTransCode,
    currentStateAbbr,
    disabled,
    fieldName,
    getTransCodes,
    getValues,
    isCollateralMortgage,
    isTransCodeRequired,
    formType,
    setValue,
    transCodeDescPropName,
    transCodePropName,
  ]);

  if (!isTransCodeRequired) return null;

  return (
    <Container>
      {disabled
        ? <TextInputField label="Trans Code" name={fieldName} disabled style={{  width: "calc(100% + 2px)"}} />
        : <ComboBoxField
          label="Trans Code"          
          {...{ name: fieldName, disabled: disabled }}
          onChange={(_: any, selectedValue: any) => {
            handleOnChange(_, selectedValue);
        }}
          options={transCodes}
          //optionsDescField="text"
          disabled={disabled}
          //onChange={handleOnChange}
          //autosetValue={false}
          roundedCorner={true}
          onBlur={handleOnBlur}
          freeSolo={true} 
        />
      }
    </Container>
  );
};

export default JacketTransCode;
